import { c as _c } from "react/compiler-runtime";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';
import { getENV, useOnClickOutside } from '@libs/common-next';
import type { Suggestions } from '@libs/experian';
import { getFormattedAddress, getStreetAddressData } from '@libs/experian/server';
type UseStreetAddressInputProps = {
  value: string;
  onAddressSelect: (address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  }) => void;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
};
export function useStreetAddressInput(t0) {
  const $ = _c(21);
  const {
    value,
    onAddressSelect,
    onChange
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [suggestions, setSuggestions] = useState(t1);
  const [inputValue, setInputValue] = useState(value);
  const selectedAddress = useRef("");
  const containerRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setSuggestions([]);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const clearSuggestions = t2;
  useOnClickOutside(containerRef, clearSuggestions);
  let t3;
  if ($[2] !== inputValue) {
    t3 = async () => {
      if (selectedAddress.current === inputValue || inputValue.length === 0 || inputValue.length < 2) {
        setSuggestions([]);
        return;
      }
      const data = await getStreetAddressData(inputValue);
      if (data?.result?.suggestions) {
        setSuggestions(data.result.suggestions);
      }
    };
    $[2] = inputValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== inputValue) {
    t4 = [inputValue];
    $[4] = inputValue;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const [, cancel] = useDebounce(t3, 400, t4);
  let t5;
  if ($[6] !== cancel) {
    t5 = () => {
      cancel();
    };
    $[6] = cancel;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[9] !== value) {
    t7 = () => {
      setInputValue(value);
    };
    t8 = [value];
    $[9] = value;
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== onChange) {
    t9 = event => {
      const newValue = event.target.value;
      setInputValue(newValue);
      onChange?.(event);
    };
    $[12] = onChange;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const handleInputChange = t9;
  let t10;
  if ($[14] !== onAddressSelect) {
    t10 = async streetAddressKey => {
      setSuggestions([]);
      const addressResponse = await getFormattedAddress(streetAddressKey);
      if (!addressResponse) {
        return;
      }
      const {
        address
      } = addressResponse.result;
      const street = address.address_line_1 + address.address_line_2 + address.address_line_3;
      let zip = address.postal_code;
      if (getENV("NEXT_PUBLIC_LOCALE") === "en-US") {
        const dashIndex = address.postal_code.indexOf("-");
        zip = dashIndex !== -1 ? address.postal_code.substring(0, dashIndex) : address.postal_code;
      }
      const state = address.region;
      const city = address.locality;
      selectedAddress.current = street;
      onAddressSelect({
        street,
        city,
        state,
        zip
      });
    };
    $[14] = onAddressSelect;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const handleAddressSelect = t10;
  let t11;
  if ($[16] !== handleAddressSelect || $[17] !== handleInputChange || $[18] !== inputValue || $[19] !== suggestions) {
    t11 = {
      inputValue,
      suggestions,
      containerRef,
      handleInputChange,
      handleAddressSelect
    };
    $[16] = handleAddressSelect;
    $[17] = handleInputChange;
    $[18] = inputValue;
    $[19] = suggestions;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
}